import { createSelector } from "reselect";

import {
  ScanNlisStatus,
  SellBlockingExceptions,
  TakeBlockingExceptions,
  TransferStatus,
} from "constants/nlis";
import { BLOCKED_PROPERTY_STATUSES } from "constants/properties";
import { UNALLOCATED } from "constants/scanner";

import { isUUID } from "lib/uuid";

import {
  selectConsignmentIdByEidLookup,
  selectEidsByConsignmentIdLookup,
  selectSaleyardScanEidsByConsignmentIdLookup,
} from "./consignmentsCalculated";
import {
  selectExceptionsByConsignmentIdLookup,
  selectExceptionsBySaleLotIdLookup,
} from "./exceptions";
import { selectEidsBySaleLotIdLookup } from "./indexes";
import { createLookupCombiner, createLookupSelectors } from "./lib";
import {
  getConsignments,
  getProperties,
  getSaleLots,
  getScans,
  getTakeFiles,
} from "./root";
import { selectBuyerHashBySaleLotIdLookup } from "./saleLots/index";
import { selectIsNoSaleBySaleLotIdLookup } from "./saleLotsCalculated";

export const [selectIsTakeableByEid, getIsTakeableByEid] =
  createLookupSelectors(
    [
      getScans,
      selectConsignmentIdByEidLookup,
      getConsignments,
      selectExceptionsByConsignmentIdLookup,
      getProperties,
    ],
    createLookupCombiner(
      (
        scan,
        consignmentIdByEIDLookup,
        consignments,
        exceptionsByConsignmentId,
        properties,
      ) => {
        if (scan.nlis_take_status !== ScanNlisStatus.UNSUBMITTED) {
          return false;
        }
        const consignmentId = consignmentIdByEIDLookup[scan.EID];
        const consignment = consignments[consignmentId];
        const property = properties[consignment?.vendor_property_id] || {};
        const vendorPic = property?.PIC;
        const propertyStatus = property?.activeStatus;

        if (
          !consignmentId ||
          !consignment ||
          !vendorPic ||
          BLOCKED_PROPERTY_STATUSES.includes(propertyStatus)
        ) {
          return false;
        }

        const exceptions = exceptionsByConsignmentId[consignmentId] || [];
        const noBlockingExceptions = exceptions.every(
          e => !TakeBlockingExceptions.includes(e),
        );

        return (
          scan.nlis_take_status === ScanNlisStatus.UNSUBMITTED &&
          noBlockingExceptions &&
          !scan.latest_take_file_id
        );
      },
    ),
  );

export const [selectIsTakeInProgressByEid, getIsInProgressByEid] =
  createLookupSelectors(
    [getScans, getTakeFiles],
    createLookupCombiner((scan, takeFiles) => {
      // Derive whether a given scan is being worked on, or not.
      // Lean to no, unless we're certain.

      // If we have no latest take file - nothing in play - we not even thinking about it.
      if (!scan.latest_take_file_id) {
        return false;
      }

      // Pending - definitely in progress, and submitted to NLIS.
      if (scan.nlis_take_status === ScanNlisStatus.PENDING) {
        return true;
      }

      // Ugly, but if we have a UUID take file - we haven't yet got a correlation from the server, so we're in progress.
      if (
        typeof scan.latest_take_file_id === "string" &&
        isUUID(scan.latest_take_file_id)
      ) {
        return true;
      }

      // Take file in syncing, or in a transient status implicates something in flight,
      if (
        takeFiles[scan.latest_take_file_id] &&
        (takeFiles[scan.latest_take_file_id].syncing ||
          [
            TransferStatus.PENDING,
            TransferStatus.SYNCING,
            TransferStatus.PREPARING_TRANSFER,
            TransferStatus.UNSUBMITTED,
          ].includes(takeFiles[scan.latest_take_file_id].status))
      ) {
        return true;
      }

      return false;
    }),
  );

export const selectTakeInProgressConsignmentCount = createSelector(
  [
    selectEidsByConsignmentIdLookup,
    selectSaleyardScanEidsByConsignmentIdLookup,
    selectIsTakeInProgressByEid,
  ],
  (
    eidsByConsignmentIdLookup,
    saleyardScanEidsByConsignmentIdLookup,
    isTakeInProgressByEid,
  ) => {
    return (
      // Scans in consignment-only (saleyard scan salelots)
      Object.keys(saleyardScanEidsByConsignmentIdLookup).filter(consignmentId =>
        saleyardScanEidsByConsignmentIdLookup[consignmentId].some(
          eid => isTakeInProgressByEid[eid],
        ),
      ).length +
      // Scans in regular salelots
      Object.keys(eidsByConsignmentIdLookup).filter(consignmentId =>
        eidsByConsignmentIdLookup[consignmentId].some(
          eid => isTakeInProgressByEid[eid],
        ),
      ).length
    );
  },
);

export const selectSightableScans = createSelector([getScans], scans =>
  Object.values(scans).filter(scan => !scan.is_nlis_sighted),
);

export const selectTakeableConsignmentIds = createSelector(
  [
    selectEidsByConsignmentIdLookup,
    selectSaleyardScanEidsByConsignmentIdLookup,
    selectIsTakeableByEid,
  ],
  (
    eidsByConsignmentIdLookup,
    saleyardScanEidsByConsignmentIdLookup,
    isTakeableByEid,
  ) =>
    [].concat(
      // Scans in consignment-only (saleyard scan salelots)
      Object.keys(saleyardScanEidsByConsignmentIdLookup).filter(consignmentId =>
        saleyardScanEidsByConsignmentIdLookup[consignmentId].some(
          eid => isTakeableByEid[eid],
        ),
      ),
      // Scans in regular salelots
      Object.keys(eidsByConsignmentIdLookup).filter(consignmentId =>
        eidsByConsignmentIdLookup[consignmentId].some(
          eid => isTakeableByEid[eid],
        ),
      ),
    ),
);
export const [selectIsSellableByEid, getIsSellableByEid] =
  createLookupSelectors(
    [
      getScans,
      getSaleLots,
      selectExceptionsByConsignmentIdLookup,
      selectExceptionsBySaleLotIdLookup,
    ],
    createLookupCombiner(
      (scan, saleLots, exceptionsByConsignmentId, exceptionsBySaleLotId) => {
        const saleLot = saleLots[scan.sale_lot_id];
        if (!scan.sale_lot_id || scan.sale_lot_id === UNALLOCATED || !saleLot) {
          return false;
        }
        const consignmentExceptions =
          exceptionsByConsignmentId[saleLot.consignment_id];
        const saleLotExceptions = exceptionsBySaleLotId[saleLot.id];

        const noBlockingTakeExceptions = consignmentExceptions?.every(
          e => !TakeBlockingExceptions.includes(e),
        );
        const noBlockingSellExceptions = saleLotExceptions?.every(
          e => !SellBlockingExceptions.includes(e),
        );

        return (
          saleLot.destination_property_id &&
          noBlockingTakeExceptions &&
          noBlockingSellExceptions &&
          (scan.nlis_take_status === ScanNlisStatus.GOOD ||
            scan.nlis_take_status === ScanNlisStatus.NEEDS_CORRECTING) &&
          !scan.latest_sell_file_id
        );
      },
    ),
  );

export const selectSellableSaleLotIds = createSelector(
  [selectEidsBySaleLotIdLookup, selectIsSellableByEid],
  (eidsBySaleLotIdLookup, isSellableByEid) =>
    Object.keys(eidsBySaleLotIdLookup).filter(saleLotId =>
      eidsBySaleLotIdLookup[saleLotId].some(eid => isSellableByEid[eid]),
    ),
);

export const selectSellableSaleLotIdsByBuyerHashLookup = createSelector(
  [selectSellableSaleLotIds, selectBuyerHashBySaleLotIdLookup],
  (sellableSaleLotIds, buyerHashBySaleLotIdLookup) => {
    return sellableSaleLotIds.reduce((acc, saleLotId) => {
      const buyerHash = buyerHashBySaleLotIdLookup[saleLotId];
      if (acc[buyerHash]) {
        acc[buyerHash] = acc[buyerHash].concat(saleLotId);
      } else {
        acc[buyerHash] = [saleLotId];
      }
      return acc;
    }, {});
  },
);

export const [selectIsP2PTransferrableByEidLookup, getIsP2PTransferrableByEid] =
  createLookupSelectors(
    [
      getScans,
      getSaleLots,
      selectExceptionsByConsignmentIdLookup,
      selectExceptionsBySaleLotIdLookup,
      selectIsNoSaleBySaleLotIdLookup,
    ],
    createLookupCombiner(
      (
        scan,
        saleLots,
        exceptionsByConsignmentId,
        exceptionsBySaleLotId,
        isNoSaleBySaleLotId,
      ) => {
        const saleLot = saleLots[scan.sale_lot_id];

        if (
          !scan.sale_lot_id ||
          scan.sale_lot_id === UNALLOCATED ||
          !saleLot ||
          isNoSaleBySaleLotId[saleLot.id]
        ) {
          return false;
        }
        const consignmentExceptions =
          exceptionsByConsignmentId[saleLot.consignment_id];
        const saleLotExceptions = exceptionsBySaleLotId[saleLot.id];

        const noBlockingTakeExceptions = consignmentExceptions?.every(
          e => !TakeBlockingExceptions.includes(e),
        );
        const noBlockingSellExceptions = saleLotExceptions?.every(
          e => !SellBlockingExceptions.includes(e),
        );

        return (
          saleLot.destination_property_id &&
          noBlockingTakeExceptions &&
          noBlockingSellExceptions &&
          scan.nlis_p2p_status === ScanNlisStatus.UNSUBMITTED &&
          !scan.latest_p2p_file_id
        );
      },
    ),
  );

// Shared per-EID eligibility check for Export P2P In/Out, parameterised
// by which status/latest-file fields to read and an optional extra
// predicate (e.g. Out depending on In having succeeded).
const buildIsExportP2PLegTransferrableByEidLookup = ({
  statusField,
  latestFileIdField,
  dependsOnGood,
}) =>
  createLookupSelectors(
    [
      getScans,
      getSaleLots,
      selectExceptionsByConsignmentIdLookup,
      selectExceptionsBySaleLotIdLookup,
      selectIsNoSaleBySaleLotIdLookup,
    ],
    createLookupCombiner(
      (
        scan,
        saleLots,
        exceptionsByConsignmentId,
        exceptionsBySaleLotId,
        isNoSaleBySaleLotId,
      ) => {
        const saleLot = saleLots[scan.sale_lot_id];

        if (
          !scan.sale_lot_id ||
          scan.sale_lot_id === UNALLOCATED ||
          !saleLot ||
          isNoSaleBySaleLotId[saleLot.id]
        ) {
          return false;
        }
        const consignmentExceptions =
          exceptionsByConsignmentId[saleLot.consignment_id];
        const saleLotExceptions = exceptionsBySaleLotId[saleLot.id];

        const noBlockingTakeExceptions = consignmentExceptions?.every(
          e => !TakeBlockingExceptions.includes(e),
        );
        const noBlockingSellExceptions = saleLotExceptions?.every(
          e => !SellBlockingExceptions.includes(e),
        );

        return (
          saleLot.destination_property_id &&
          noBlockingTakeExceptions &&
          noBlockingSellExceptions &&
          // A missing NLISStatus row (no status yet recorded) is treated as
          // unsubmitted/eligible, not excluded.
          (!scan[statusField] ||
            scan[statusField] === ScanNlisStatus.UNSUBMITTED) &&
          !scan[latestFileIdField] &&
          (dependsOnGood ? dependsOnGood(scan) : true)
        );
      },
    ),
  );

export const [
  selectIsExportP2PInTransferrableByEidLookup,
  getIsExportP2PInTransferrableByEid,
] = buildIsExportP2PLegTransferrableByEidLookup({
  statusField: "nlis_export_p2p_in_status",
  latestFileIdField: "latest_export_p2p_in_file_id",
});

export const [
  selectIsExportP2POutTransferrableByEidLookup,
  getIsExportP2POutTransferrableByEid,
] = buildIsExportP2PLegTransferrableByEidLookup({
  statusField: "nlis_export_p2p_out_status",
  latestFileIdField: "latest_export_p2p_out_file_id",
  // The animal must be on the saleyard's PIC first, via P2P In or a
  // regular Take. Mirrors Sell's dependency on nlis_take_status.
  dependsOnGood: scan =>
    scan.nlis_export_p2p_in_status === ScanNlisStatus.GOOD ||
    scan.nlis_take_status === ScanNlisStatus.GOOD,
});
